import { useState, useEffect, useCallback } from 'react';

export const usePWA = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isInstalled, setIsInstalled] = useState(false);
  const [canInstall, setCanInstall] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [registration, setRegistration] = useState(null);

  // Проверка статуса подключения
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Функция проверки обновлений Service Worker
  const checkForUpdates = useCallback(async (reg) => {
    if (!reg) return;
    
    try {
      // Принудительная проверка обновлений Service Worker
      await reg.update();
    } catch (error) {
    }
  }, []);

  // Регистрация Service Worker
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      // Проверяем, существует ли файл sw.js перед регистрацией
      fetch('/sw.js', { method: 'HEAD' })
        .then((response) => {
          if (response.ok) {
            // Файл существует, регистрируем Service Worker
            return navigator.serviceWorker.register('/sw.js');
          } else {
            // Файл не найден, не регистрируем
            throw new Error('Service Worker file not found');
          }
        })
        .then((reg) => {
          setRegistration(reg);
          
          // Проверяем обновления при регистрации
          if (reg.waiting) {
            // Уже есть ожидающий Service Worker
            setUpdateAvailable(true);
          }
          
          // Проверяем обновления при установке нового
          reg.addEventListener('updatefound', () => {
            const newWorker = reg.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed') {
                  // Новый Service Worker установлен
                  if (navigator.serviceWorker.controller) {
                    // Есть активный Service Worker, значит это обновление
                    setUpdateAvailable(true);
                  }
                }
              });
            }
          });
          
          // Слушаем изменения состояния регистрации
          reg.addEventListener('controllerchange', () => {
            // Service Worker активирован, обновление завершено
            setUpdateAvailable(false);
            window.location.reload();
          });

          // Периодическая проверка обновлений (каждые 5 минут)
          const updateInterval = setInterval(() => {
            checkForUpdates(reg);
          }, 5 * 60 * 1000); // 5 минут

          // Проверка обновлений при фокусе окна (когда пользователь возвращается)
          const handleFocus = () => {
            checkForUpdates(reg);
          };
          window.addEventListener('focus', handleFocus);

          // Проверка обновлений при восстановлении соединения
          const handleOnline = () => {
            checkForUpdates(reg);
          };
          window.addEventListener('online', handleOnline);

          // Очистка при размонтировании
          return () => {
            clearInterval(updateInterval);
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('online', handleOnline);
          };
        })
        .catch((error) => {
          // Не логируем ошибку, если файл просто не найден (404)
          // Это нормально, если Service Worker не настроен
          if (!error.message?.includes('not found') && !error.message?.includes('404')) {
          }
        });
    }
  }, [checkForUpdates]);

  // Проверка установки PWA
  useEffect(() => {
    const checkInstallation = () => {
      // Проверяем различные способы определения установки PWA
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isInStandaloneMode = ('standalone' in window.navigator) && window.navigator.standalone;
      
      // Дополнительные проверки для Opera и других браузеров
      const userAgent = navigator.userAgent || '';
      const isOpera = /OPR|Opera|OPiOS|OPAndroid/.test(userAgent) || 
                     (window.opr && window.opr.addons) || 
                     (window.opera && window.opera.toString().indexOf('Opera') !== -1);
      
      // Проверяем, нет ли адресной строки (признак установленного PWA)
      // НО: это ненадежный способ, так как в обычном браузере тоже может не быть адресной строки
      // Используем только для дополнительной проверки, не как основной критерий
      const isFullscreen = window.matchMedia('(display-mode: fullscreen)').matches;
      const isMinimalUI = window.matchMedia('(display-mode: minimal-ui)').matches;
      
      // Основной критерий - display-mode: standalone
      // Для iOS - navigator.standalone
      // Для Opera виджетов - только если явно standalone, не используем хак с размерами окна
      const installed = isStandalone || 
                       (isIOS && isInStandaloneMode);
      
      // fullscreen и minimal-ui могут быть в обычном браузере, поэтому не используем их как признак установки
      
      
      setIsInstalled(installed);
      
      // Если установлено, сохраняем в localStorage для быстрой проверки
      if (installed) {
        localStorage.setItem('pwa_installed', 'true');
        localStorage.setItem('pwa_installed_timestamp', Date.now().toString());
      }
    };

    // Проверяем сразу
    checkInstallation();
    
    // Проверяем установку из localStorage (быстрая проверка)
    // НО: не полагаемся только на localStorage, так как он может быть неточным
    // Проверяем только если есть timestamp и он не старше 24 часов
    const storedInstalled = localStorage.getItem('pwa_installed') === 'true';
    const storedTimestamp = localStorage.getItem('pwa_installed_timestamp');
    const isStoredRecent = storedTimestamp && (Date.now() - parseInt(storedTimestamp, 10)) < 86400000; // 24 часа
    
    if (storedInstalled && isStoredRecent) {
      // Проверяем еще раз через display-mode, чтобы убедиться
      const isStandaloneCheck = window.matchMedia('(display-mode: standalone)').matches;
      if (isStandaloneCheck) {
        setIsInstalled(true);
      } else {
        // Если в localStorage помечено как установлено, но display-mode не standalone,
        // возможно это была ошибка - очищаем localStorage
        localStorage.removeItem('pwa_installed');
        localStorage.removeItem('pwa_installed_timestamp');
      }
    }
    
    // Слушаем изменения display-mode
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const mediaQueryFullscreen = window.matchMedia('(display-mode: fullscreen)');
    const mediaQueryMinimalUI = window.matchMedia('(display-mode: minimal-ui)');
    
    const handleMediaChange = () => {
      checkInstallation();
    };
    
    mediaQuery.addEventListener('change', handleMediaChange);
    mediaQueryFullscreen.addEventListener('change', handleMediaChange);
    mediaQueryMinimalUI.addEventListener('change', handleMediaChange);
    
    // Периодическая проверка (каждые 2 секунды) для Opera виджетов
    // Виджеты Opera могут не сразу переводить в standalone режим
    const checkInterval = setInterval(() => {
      checkInstallation();
    }, 2000);
    
    // Проверка при изменении размера окна (виджет может изменить размер)
    const handleResize = () => {
      checkInstallation();
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    return () => {
      mediaQuery.removeEventListener('change', handleMediaChange);
      mediaQueryFullscreen.removeEventListener('change', handleMediaChange);
      mediaQueryMinimalUI.removeEventListener('change', handleMediaChange);
      clearInterval(checkInterval);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  // Обработка события beforeinstallprompt
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      const userAgent = navigator.userAgent || '';
      const isOpera = /OPR|Opera|OPiOS|OPAndroid/.test(userAgent) || 
                     (window.opr && window.opr.addons) || 
                     (window.opera && window.opera.toString().indexOf('Opera') !== -1);
      
      
      // Сохраняем информацию о срабатывании события
      const eventInfo = {
        timestamp: Date.now(),
        userAgent,
        isOpera,
        platforms: e.platforms || [],
        url: window.location.href,
        referrer: document.referrer
      };
      
      localStorage.setItem('pwa_beforeinstallprompt_fired', 'true');
      localStorage.setItem('pwa_beforeinstallprompt_info', JSON.stringify(eventInfo));
      localStorage.setItem('pwa_beforeinstallprompt_count', 
        (parseInt(localStorage.getItem('pwa_beforeinstallprompt_count') || '0', 10) + 1).toString()
      );
      
      
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstall(true);
    };

    // Проверяем историю срабатываний события
    const hasFiredBefore = localStorage.getItem('pwa_beforeinstallprompt_fired') === 'true';
    const fireCount = parseInt(localStorage.getItem('pwa_beforeinstallprompt_count') || '0', 10);
    const lastFireInfo = localStorage.getItem('pwa_beforeinstallprompt_info');
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Проверяем, может ли браузер показать промпт установки
    // В некоторых браузерах (особенно Opera) событие может не сработать сразу
    // но может сработать позже при определенных условиях
    const userAgent = navigator.userAgent || '';
    const isOpera = /OPR|Opera|OPiOS|OPAndroid/.test(userAgent) || 
                   (window.opr && window.opr.addons) || 
                   (window.opera && window.opera.toString().indexOf('Opera') !== -1);
    
    // Для Opera: если событие срабатывало раньше, значит оно может сработать снова

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // Обработка события appinstalled
  useEffect(() => {
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setCanInstall(false);
      setDeferredPrompt(null);
      // Сохраняем в localStorage для быстрой проверки
      localStorage.setItem('pwa_installed', 'true');
      localStorage.setItem('pwa_installed_timestamp', Date.now().toString());
      // Сбрасываем флаг попытки установки при успешной установке
      localStorage.removeItem('pwa_install_attempted_timestamp');
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Установка PWA
  const installPWA = useCallback(async () => {
    if (!deferredPrompt) {
      return false;
    }

    try {
      // Проверяем, есть ли метод prompt
      if (typeof deferredPrompt.prompt !== 'function') {
        throw new Error('Install prompt method not available');
      }
      
      // Показываем нативный промпт браузера
      deferredPrompt.prompt();
      
      // Проверяем, есть ли userChoice
      if (!deferredPrompt.userChoice) {
        throw new Error('User choice promise not available');
      }
      
      // Ждем ответа пользователя (с таймаутом на случай зависания)
      const choicePromise = deferredPrompt.userChoice;
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Install prompt timeout')), 30000)
      );
      
      const { outcome } = await Promise.race([choicePromise, timeoutPromise]);
      
      // Очищаем deferredPrompt независимо от результата
      setDeferredPrompt(null);
      
      if (outcome === 'accepted') {
        setCanInstall(false);
        return true;
      } else {
        setCanInstall(false);
        return false;
      }
    } catch (error) {
      setDeferredPrompt(null);
      setCanInstall(false);
      return false;
    }
  }, [deferredPrompt, isInstalled]);

  // Обновление приложения
  const updateApp = useCallback(() => {
    if (!registration) {
      return;
    }
    
    // Проверяем waiting (установленный, но не активированный Service Worker)
    if (registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      // Не перезагружаем сразу - дождемся активации через controllerchange
      return;
    }
    
    // Проверяем installing (устанавливающийся Service Worker)
    if (registration.installing) {
      const installingWorker = registration.installing;
      installingWorker.addEventListener('statechange', () => {
        if (installingWorker.state === 'installed' && navigator.serviceWorker.controller) {
          installingWorker.postMessage({ type: 'SKIP_WAITING' });
        }
      });
      return;
    }
    
    // Если нет waiting или installing, просто перезагружаем страницу
    window.location.reload();
  }, [registration]);

  // Отправка сообщения в Service Worker
  const sendMessageToSW = useCallback((message) => {
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage(message);
    }
  }, []);

  // Кэширование URL
  const cacheUrls = useCallback((urls) => {
    sendMessageToSW({ type: 'CACHE_URLS', urls });
  }, [sendMessageToSW]);

  // Получение информации о кэше
  const getCacheInfo = useCallback(async () => {
    if (!('caches' in window)) {
      return null;
    }

    try {
      const cacheNames = await caches.keys();
      const cacheInfo = {};

      for (const cacheName of cacheNames) {
        const cache = await caches.open(cacheName);
        const requests = await cache.keys();
        cacheInfo[cacheName] = {
          size: requests.length,
          urls: requests.map(req => req.url)
        };
      }

      return cacheInfo;
    } catch (error) {
      return null;
    }
  }, []);

  // Очистка кэша
  const clearCache = useCallback(async () => {
    if (!('caches' in window)) {
      return false;
    }

    try {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
      return true;
    } catch (error) {
      return false;
    }
  }, []);

  // Проверка поддержки PWA функций
  const getPWASupport = useCallback(() => {
    return {
      serviceWorker: 'serviceWorker' in navigator,
      pushNotifications: 'PushManager' in window,
      backgroundSync: 'serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype,
      cacheAPI: 'caches' in window,
      installPrompt: 'BeforeInstallPromptEvent' in window,
      shareAPI: 'share' in navigator,
      fileSystem: 'showOpenFilePicker' in window,
      webShareTarget: 'serviceWorker' in navigator
    };
  }, []);

  // Получение информации об устройстве
  const getDeviceInfo = useCallback(() => {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      languages: navigator.languages,
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
      hardwareConcurrency: navigator.hardwareConcurrency,
      deviceMemory: navigator.deviceMemory,
      connection: navigator.connection ? {
        effectiveType: navigator.connection.effectiveType,
        downlink: navigator.connection.downlink,
        rtt: navigator.connection.rtt,
        saveData: navigator.connection.saveData
      } : null
    };
  }, []);

  // Функция для ручного подтверждения установки (для случаев, когда автоматическое определение не сработало)
  const markAsInstalled = useCallback(() => {
    setIsInstalled(true);
    setCanInstall(false);
    setDeferredPrompt(null);
    // Сохраняем в localStorage
    localStorage.setItem('pwa_installed', 'true');
    localStorage.setItem('pwa_installed_timestamp', Date.now().toString());
    localStorage.removeItem('pwa_install_attempted_timestamp');
  }, []);

  return {
    // Состояние
    isOnline,
    isInstalled,
    canInstall,
    updateAvailable,
    registration,
    
    // Методы
    installPWA,
    updateApp,
    checkForUpdates,
    sendMessageToSW,
    cacheUrls,
    getCacheInfo,
    clearCache,
    getPWASupport,
    getDeviceInfo,
    markAsInstalled
  };
};
